import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@apollo/client';
import { Loader2Icon } from 'lucide-react';

import {
  Button,
  Dialog,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  MonthYearPicker,
  useErrorHandler,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  CreatePriorCertificationDocument,
  DeletePriorCertificationDocument,
  PriorCertification,
  UpdatePriorCertificationDocument,
} from '@sandtech-hub-monorepo/ehub-client';

import { PriorCertificationDeleteAlertDialog } from './PriorCertificationDeleteAlertDialog';
import { titleLikeField } from '../../../utils/validation';

const formSchema = z.object({
  institute: titleLikeField('Institute', 100),
  courseName: titleLikeField('Course name', 150),
  completedAt: z.date({ required_error: 'Year is required' }),
  verificationUrl: z
    .union([z.literal(''), z.string().url('Enter a valid URL')])
    .optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  handleOpenChange: (open: boolean) => void;
  certification?: PriorCertification;
}

export const PriorCertificationEditDialog = ({
  open,
  handleOpenChange,
  certification,
}: Props) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { handleGeneralAppError } = useErrorHandler();

  const [createPriorCertification, { loading: createLoading }] = useMutation(
    CreatePriorCertificationDocument,
    {
      onError: (error) => handleGeneralAppError(error.message),
      onCompleted: () => handleOpenChange(false),
    }
  );

  const [updatePriorCertification, { loading: updateLoading }] = useMutation(
    UpdatePriorCertificationDocument,
    {
      onError: (error) => handleGeneralAppError(error.message),
      onCompleted: () => handleOpenChange(false),
    }
  );

  const [deletePriorCertification, { loading: deleteLoading }] = useMutation(
    DeletePriorCertificationDocument,
    {
      onError: (error) => handleGeneralAppError(error.message),
      onCompleted: () => {
        setDeleteDialogOpen(false);
        handleOpenChange(false);
      },
    }
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  // See ProfileEditDialog for why this is keyed on `open` rather than `certification`.
  useEffect(() => {
    if (open) {
      form.reset({
        institute: certification?.institute || '',
        courseName: certification?.courseName || '',
        completedAt: certification?.completedAt
          ? new Date(certification.completedAt)
          : undefined,
        verificationUrl: certification?.verificationUrl || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (values: FormValues) => {
    const priorCertification = {
      institute: values.institute,
      courseName: values.courseName,
      completedAt: values.completedAt,
      // `null`, not `undefined`: an update omits `undefined` keys entirely (no
      // change), but editing this field blank must actually clear a
      // previously-set URL rather than leave the old one in place.
      verificationUrl: values.verificationUrl || null,
    };

    if (certification) {
      updatePriorCertification({
        variables: {
          input: {
            priorCertification: { ...priorCertification, id: certification.id },
          },
        },
      });
    } else {
      createPriorCertification({
        variables: { input: { priorCertification } },
      });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        title={
          certification
            ? 'Edit Prior Certification'
            : 'Add Prior Certifications'
        }
        className="bg-card"
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="institute"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Institute</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Institute Name eg. Google"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="completedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Year</FormLabel>
                    <FormControl>
                      <MonthYearPicker
                        selected={field.value}
                        onSelect={field.onChange}
                        placeholder="Select Month/Year"
                        disabledDates={(date) => date.valueOf() > Date.now()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="courseName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">Course name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter Course name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="verificationUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">
                    Verification URL (optional)
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Where can this certification be verified?"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="mt-4 flex flex-col sm:flex-row gap-1">
              {certification && (
                <Button
                  type="button"
                  variant="link"
                  className="sm:mr-auto text-destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete Prior Certification
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createLoading || updateLoading}>
                {createLoading || updateLoading ? (
                  <Loader2Icon className="animate-spin" />
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </Dialog>

      {certification && (
        <PriorCertificationDeleteAlertDialog
          open={deleteDialogOpen}
          onCancel={() => setDeleteDialogOpen(false)}
          onConfirm={() =>
            deletePriorCertification({
              variables: { input: { id: certification.id } },
            })
          }
          loading={deleteLoading}
        />
      )}
    </>
  );
};
